/**
 * Renderings - port from `1116/omega-club/dashboard/src/components/Renderings.jsx`.
 *
 * Per CHAP-89 (Phase 1C #55).
 *
 * Data-source decision: omega-club reads a hardcoded `renderingsData` array.
 * chapter-build has no `tenant_renderings` table, and adding one is out of
 * scope for this ticket. Instead this page reuses the existing
 * `dataroom_documents` table (already DB-backed, already tenant-scoped,
 * already sensitivity-gated) filtered to `category = 'architectural'` -
 * exactly the fallback the schema anticipates (`intake_category` enum
 * already lists `'architectural'`; see `enums.ts`). The same
 * `GET /api/c/:slug/documents` endpoint and `useDocuments` hook that power
 * the DataRoom page (CHAP-95/96/97) drive this page; download uses the
 * same signed-URL flow (`useDownloadDocument`) instead of the omega-club
 * Worker URL.
 *
 * Limitation: omega-club's rendering cards show a raster preview image
 * (`item.preview`) tied to each rendering. `dataroom_documents` has no
 * dedicated preview-thumbnail column, so this page shows a document-type
 * icon in place of a thumbnail instead of an image preview. A preview
 * thumbnail is a schema addition, out of scope here.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { useDocuments, useDownloadDocument } from '../hooks/useDocuments.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import type { DataroomDocument } from '../../shared/types.js';

function BlueprintIcon() {
  return (
    <svg
      className="w-14 h-14 text-white/30"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={0.75}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21"
      />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      className="w-3.5 h-3.5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Undated';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function Renderings() {
  const { slug } = useParams<{ slug: string }>();
  const tenantSlug = slug ?? '';
  const { data: tenantData } = useTenantData(tenantSlug);
  const { data, isLoading, error, refetch } = useDocuments(tenantSlug);
  const download = useDownloadDocument(tenantSlug);

  const renderings = useMemo<DataroomDocument[]>(() => {
    if (!data) return [];
    return data.documents
      .filter((doc) => doc.category === 'architectural')
      .sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return (b.dateOfRecord ?? '').localeCompare(a.dateOfRecord ?? '');
      });
  }, [data]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;

  return (
    <div className="space-y-3 sm:space-y-4 min-w-0">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Architectural Vision</h2>
        <p className="text-neutral text-sm sm:text-base mt-1">
          Renderings and plans for {tenantData?.tenant.displayName ?? 'this chapter'}
        </p>
      </div>

      {renderings.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8 text-center min-w-0">
          <p className="text-neutral text-sm">
            No architectural renderings have been uploaded to the data room yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 min-w-0">
          {renderings.map((doc) => (
            <RenderingCard
              key={doc.id}
              doc={doc}
              onView={() => download.mutate(doc.id)}
              isDownloading={download.isPending && download.variables === doc.id}
            />
          ))}
        </div>
      )}

      {data && data.hiddenCount > 0 && (
        <p className="text-xs text-neutral italic px-2">
          {data.hiddenCount} additional data room document
          {data.hiddenCount === 1 ? '' : 's'} hidden - your access tier doesn't include them.
        </p>
      )}
    </div>
  );
}

function RenderingCard({
  doc,
  onView,
  isDownloading,
}: {
  doc: DataroomDocument;
  onView: () => void;
  isDownloading: boolean;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow min-w-0">
      {/* Preview panel - no thumbnail column on dataroom_documents yet, so
          this shows a type icon on a gradient panel rather than an image. */}
      <div
        className="relative h-40 sm:h-48 overflow-hidden flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #03244d 0%, #0a3a6b 50%, #03244d 100%)' }}
      >
        <BlueprintIcon />
        {doc.isPrimary && (
          <span className="absolute top-3 left-3 bg-orange text-white text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-sm">
            Current
          </span>
        )}
        {doc.fileType && (
          <span className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full uppercase">
            {doc.fileType}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 min-w-0">
        <h3 className="text-navy font-bold text-base sm:text-lg truncate">{doc.name}</h3>
        {doc.description && (
          <p className="text-neutral text-xs sm:text-sm mt-1.5 leading-relaxed">{doc.description}</p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-neutral min-w-0">
          <span className="truncate">{doc.sourceAttribution ?? 'Unknown source'}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
          <span className="flex-shrink-0">{formatDate(doc.dateOfRecord)}</span>
        </div>
        <button
          type="button"
          onClick={onView}
          disabled={isDownloading}
          className="mt-4 flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-orange hover:bg-orange-dark text-white text-sm font-medium transition-colors disabled:opacity-50"
        >
          <ExternalLinkIcon />
          {isDownloading ? 'Opening...' : 'View Document'}
        </button>
      </div>
    </div>
  );
}
